import { wikiToPlainText } from '@/util/tokenize_wiki_text'
import type { GoalHit } from '@/goals/goal_types'

export type DraftedStatement = {
  skill_name: string
  statement_text: string
}

export type StatementsBySubject = Record<string, DraftedStatement[]>

export type StatementsByArea = {
  strengths?: StatementsBySubject
  needs?: StatementsBySubject
}

export type SkillNamesBySubject = Record<string, string[]>

export type DraftedSkills = {
  strengths: SkillNamesBySubject
  needs: SkillNamesBySubject
}

export type RelevantGoalsQuery = {
  search_text: string
  subjects: string[]
  domains?: string[]
  grade?: string
}

export type GoalRecommendation = {
  subject: string
  category: string
  grades?: string[]
  goals?: GoalHit[]
}

const READING = 'Reading'

const GOAL_SUBJECTS_BY_PLW_SUBJECT: Record<string, string[]> = {
  'Pre-Academic Skills': ['Reading', 'Math', 'Pre-Academics', 'Fine Motor', 'Autism', 'Speech & Language', 'Executive Functioning'],
  Reading: ['Reading'],
  Writing: ['Writing'],
  Math: ['Math'],
  'Alt Academics: Reading': ['Alt Academics'],
  'Alt Academics: Writing': ['Alt Academics'],
  'Alt Academics: Math': ['Alt Academics'],
  'Executive Functioning': ['Executive Functioning', 'Autism', 'Pre-Academics'],
  Behavior: ['Behavior & SEL'],
  'Social & Emotional Learning': ['Behavior & SEL'],
  Communication: ['Speech & Language', 'Autism', 'Pre-Academics', 'Assistive Technology'],
  'Fine Motor Skills': ['Fine Motor', 'Pre-Academics'],
  'Gross Motor Skills': ['Gross Motor', 'Pre-Academics', 'Adapted Physical Education'],
  Functional: ['Life Skills', 'Autism', 'Pre-Academics', 'Adapted Physical Education', 'Transition'],
  Transition: ['Transition', 'Autism', 'Life Skills', 'Executive Functioning', 'Fine Motor']
}

const GOAL_DOMAINS_BY_READING_SKILL: Record<string, string[]> = {
  'Concepts of Print': ['Concepts of Print'],
  // Phonological Awareness is being split out of Phonics & Decoding/Vocabulary into its own goal domain;
  // keep the fallback until existing goals are retagged (see Reading & Transition Category Updates project).
  'Phonological Awareness': ['Phonological Awareness', 'Phonics & Decoding', 'Vocabulary'],
  'Letter/Sound Identification': ['Concepts of Print', 'Phonics & Decoding'],
  'High Frequency Word Reading': ['Phonics & Decoding', 'Fluency', 'Comprehension: Informational Text', 'Comprehension: Literature'],
  'Phonics & Decoding': ['Phonics & Decoding', 'Comprehension: Informational Text', 'Comprehension: Literature'],
  Fluency: ['Fluency', 'Comprehension: Informational Text', 'Comprehension: Literature'],
  Comprehension: ['Comprehension: Literature', 'Comprehension: Informational Text'],
  'Language Structures': ['Language Structures']
}

const SUBJECTS_WITHOUT_GRADE_FILTER = [
  'Pre-Academic Skills',
  'Fine Motor Skills',
  'Gross Motor Skills',
  'Functional',
  'Executive Functioning',
  'Communication',
  'Transition'
]

const BROWSE_SUBJECT_BY_PLW_SUBJECT: Record<string, string> = {
  'Pre-Academic Skills': 'Pre-Academics',
  Reading: 'Reading',
  Writing: 'Writing',
  Math: 'Math',
  'Alt Academics: Reading': 'Alt Academics',
  'Alt Academics: Writing': 'Alt Academics',
  'Alt Academics: Math': 'Alt Academics',
  'Executive Functioning': 'Executive Functioning',
  Behavior: 'Behavior & SEL',
  'Social & Emotional Learning': 'Behavior & SEL',
  Communication: 'Speech & Language',
  'Fine Motor Skills': 'Fine Motor',
  'Gross Motor Skills': 'Gross Motor',
  Functional: 'Life Skills',
  Transition: 'Transition'
}

export function goalSubjectsForPlwSubject(subject: string): string[] {
  return GOAL_SUBJECTS_BY_PLW_SUBJECT[subject] ?? []
}

export function goalDomainsForReadingSkill(skill: string): string[] {
  return GOAL_DOMAINS_BY_READING_SKILL[skill] ?? []
}

export function hasGradeFilterForAlignedGoals(subject: string): boolean {
  return !SUBJECTS_WITHOUT_GRADE_FILTER.includes(subject)
}

export function browseSubjectForPlwSubject(subject: string): string | undefined {
  return BROWSE_SUBJECT_BY_PLW_SUBJECT[subject]
}

function relevantGoalsQuery(subject: string, statement: DraftedStatement, grade: string): RelevantGoalsQuery {
  const query: RelevantGoalsQuery = {
    search_text: wikiToPlainText(statement.statement_text),
    subjects: goalSubjectsForPlwSubject(subject)
  }
  if (subject === READING) {
    query.domains = goalDomainsForReadingSkill(statement.skill_name)
  }
  if (hasGradeFilterForAlignedGoals(subject)) {
    query.grade = grade
  }
  return query
}

function emptyRecommendation(subject: string, statement: DraftedStatement, grade: string): GoalRecommendation {
  const recommendation: GoalRecommendation = { subject, category: statement.skill_name }
  if (hasGradeFilterForAlignedGoals(subject)) {
    recommendation.grades = [grade]
  }
  return recommendation
}

// Goals are only recommended for areas of need, and the API answers each query at the same index as its recommendation.
export function buildGoalQueriesAndRecommendations(statementsByArea: StatementsByArea, grade: string) {
  const queries: RelevantGoalsQuery[] = []
  const recommendations: GoalRecommendation[] = []
  for (const [subject, statements] of Object.entries(statementsByArea.needs ?? {})) {
    for (const statement of statements) {
      queries.push(relevantGoalsQuery(subject, statement, grade))
      recommendations.push(emptyRecommendation(subject, statement, grade))
    }
  }
  return { queries, recommendations }
}

export function skillNamesBySubject(statementsBySubject: StatementsBySubject = {}): SkillNamesBySubject {
  const skillNames: SkillNamesBySubject = {}
  for (const [subject, statements] of Object.entries(statementsBySubject)) {
    skillNames[subject] = statements.map((statement) => statement.skill_name)
  }
  return skillNames
}
